import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  CreditCard,
  Group,
  People,
  AssignmentTurnedIn,
  NotificationsActive,
  Build,
  Schedule,
  Warning,
  DirectionsBus,
  Paragliding,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isAdmin, isClubUser } from '../../utils/auth';
import api from '../../services/api';
import { ClubWithCounts } from '../../types';
import EmailVerificationPrompt from '../../components/auth/EmailVerificationPrompt';
import { useTranslation } from 'react-i18next';
import CompactActivityLog from '../../components/activities/CompactActivityLog';
import PlatformNews from '../../components/dashboard/PlatformNews';

interface DashboardStats {
  totalCards: number;
  activeCards: number;
  totalClubs: number;
  totalUsers: number;
}

interface ReminderStats {
  totalSubscriptions: number;
  activeSubscriptions: number;
  upcomingReminders: number;
  overdueItems: number;
  sentToday: number;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  
  // All hooks must be called before any conditional returns
  const [stats, setStats] = useState<DashboardStats>({
    totalCards: 0,
    activeCards: 0,
    totalClubs: 0,
    totalUsers: 0,
  });
  const [reminderStats, setReminderStats] = useState<ReminderStats>({
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    upcomingReminders: 0,
    overdueItems: 0,
    sentToday: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(true);

  useEffect(() => {
    // Security is now handled by SecureDashboardRouter wrapper
    // This component can focus on functionality
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Try to fetch clubs with counts, but don't fail if user has no clubs
      try {
        const response = await api.get<ClubWithCounts[]>('/clubs-with-counts');
        const clubs = response.data;
        
        // Calculate totals from the clubs the user has access to
        const totalCards = clubs.reduce((sum, club) => sum + club.total_cards, 0);
        const activeCards = clubs.reduce((sum, club) => sum + club.active_cards, 0);
        
        setStats({
          totalCards,
          activeCards,
          totalClubs: clubs.length,
          totalUsers: 0, // This would need a separate API call for admin users
        });
      } catch (clubError: any) {
        // If 404 or 403, user just doesn't have clubs yet - that's ok
        if (clubError.response?.status === 404 || clubError.response?.status === 403) {
          setStats({
            totalCards: 0,
            activeCards: 0,
            totalClubs: 0,
            totalUsers: 0,
          });
        } else {
          // Re-throw if it's a different error
          throw clubError;
        }
      }
      
      // If admin, fetch user count and reminder stats
      if (isAdmin()) {
        try {
          const usersResponse = await api.get('/users');
          setStats(prev => ({ ...prev, totalUsers: usersResponse.data.length }));
          
          // Fetch reminder statistics
          const reminderResponse = await api.get('/reminders/admin/dashboard');
          if (reminderResponse.data) {
            setReminderStats({
              totalSubscriptions: reminderResponse.data.total_subscriptions || 0,
              activeSubscriptions: reminderResponse.data.active_subscriptions || 0,
              upcomingReminders: reminderResponse.data.upcoming_reminders_7_days || 0,
              overdueItems: reminderResponse.data.overdue_items || 0,
              sentToday: reminderResponse.data.notifications_sent_today || 0,
            });
          }
        } catch (error) {
          // Failed to fetch admin data - silent fail
        }
      }
    } catch (error) {
      // Set some default values on error
      setStats({
        totalCards: 0,
        activeCards: 0,
        totalClubs: 0,
        totalUsers: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Security is now handled by SecureDashboardRouter wrapper
  // No need for client-side permission checking here

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `${color}10`,
        }
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                mb: 1 
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: `linear-gradient(45deg, ${color} 30%, ${color}dd 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box 
            sx={{ 
              color,
              backgroundColor: `${color}15`,
              borderRadius: 3,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Check if user needs to verify email
  const needsEmailVerification = user && !user.is_active;

  return (
    <Container maxWidth="lg" sx={{ mt: 3 }}>
      {/* Show email verification prompt if needed */}
        {needsEmailVerification && showVerificationPrompt && (
          <EmailVerificationPrompt onClose={() => setShowVerificationPrompt(false)} />
        )}

      {/* Statistics Cards */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
          <StatCard
            title={t('dashboard.stats.totalCards')}
            value={stats.totalCards}
            icon={<CreditCard fontSize="large" />}
            color="#3388ff"
          />
          <StatCard
            title={t('dashboard.stats.activeCards')}
            value={stats.activeCards}
            icon={<AssignmentTurnedIn fontSize="large" />}
            color="#00c853"
          />
          {(isAdmin() || isClubUser()) && (
            <>
              <StatCard
                title={t('dashboard.stats.totalClubs')}
                value={stats.totalClubs}
                icon={<Group fontSize="large" />}
                color="#ff00fb"
              />
              {isAdmin() && (
                <StatCard
                  title={t('dashboard.stats.totalUsers')}
                  value={stats.totalUsers}
                  icon={<People fontSize="large" />}
                  color="#ff4444"
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Reminder Service Overview - Admin Only */}
      {isAdmin() && (
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea15 0%, #764ba205 100%)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationsActive sx={{ mr: 2, color: '#667eea' }} />
              <Typography variant="h6">
                {t('dashboard.reminderService.title')}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Schedule />}
              onClick={() => navigate('/app/admin/reminders')}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5a67d8',
                  backgroundColor: 'rgba(102, 126, 234, 0.04)',
                },
              }}
            >
              {t('dashboard.reminderService.viewDetails')}
            </Button>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <People sx={{ mr: 1, color: '#667eea', fontSize: 20 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.reminderService.activeUsers')}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {reminderStats.activeSubscriptions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('common.of')} {reminderStats.totalSubscriptions} {t('common.total')}
              </Typography>
            </Box>

            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Schedule sx={{ mr: 1, color: '#3388ff', fontSize: 20 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.reminderService.upcoming')}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="info.main">
                {reminderStats.upcomingReminders}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.reminderService.next7days')}
              </Typography>
            </Box>

            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              backgroundColor: reminderStats.overdueItems > 0 ? '#fff5f5' : 'background.paper',
              border: '1px solid',
              borderColor: reminderStats.overdueItems > 0 ? '#ffcccc' : 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning sx={{ mr: 1, color: '#ff4444', fontSize: 20 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.reminderService.overdue')}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color={reminderStats.overdueItems > 0 ? 'error' : 'text.primary'}>
                {reminderStats.overdueItems}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.reminderService.needsAttention')}
              </Typography>
            </Box>

            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Build sx={{ mr: 1, color: '#00c853', fontSize: 20 }} />
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.reminderService.sentToday')}
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {reminderStats.sentToday}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.reminderService.notifications')}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('dashboard.quickActions')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<CreditCard />}
            onClick={() => navigate('/app/cards')}
          >
            {t('dashboard.viewCards')}
          </Button>
          {/* Club management buttons - shown for all logged in users */}
          {user && (
            <>
              {/* Import Cards - only for platform admin */}
              {isAdmin() && (
                <Button
                  variant="outlined"
                  startIcon={<CreditCard />}
                  onClick={() => navigate('/app/cards/import')}
                >
                  {t('dashboard.importCards')}
                </Button>
              )}
              {/* Visitor Cards - for users with clubs */}
              {(isAdmin() || (user?.clubs && user.clubs.length > 0)) && (
                <Button
                  variant="outlined"
                  startIcon={<People />}
                  onClick={() => navigate('/app/visitors')}
                  sx={{
                    borderColor: '#ff00fb',
                    color: '#ff00fb',
                    '&:hover': {
                      borderColor: '#cc00c8',
                      backgroundColor: 'rgba(255, 0, 251, 0.04)',
                    },
                  }}
                >
                  {t('dashboard.VisitorCards')}
                </Button>
              )}
              {/* Shuttle Management - for users with clubs */}
              {(isAdmin() || (user?.clubs && user.clubs.length > 0)) && (
                <Button
                  variant="outlined"
                  startIcon={<DirectionsBus />}
                  onClick={() => navigate('/app/shuttles')}
                >
                  {t('dashboard.manageShuttles')}
                </Button>
              )}
              {/* Manage Clubs and Create Club - shown for all users */}
              <Button
                variant="outlined"
                startIcon={<Group />}
                onClick={() => navigate('/app/admin/clubs')}
              >
                {t('dashboard.manageClubs')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Group />}
                onClick={() => navigate('/app/admin/create-club')}
                sx={{
                  borderColor: '#ff00fb',
                  color: '#ff00fb',
                  '&:hover': {
                    borderColor: '#cc00c8',
                    backgroundColor: 'rgba(255, 0, 251, 0.04)',
                  },
                }}
              >
                {t('dashboard.createClub')}
              </Button>
            </>
          )}
          {/* Admin-only buttons */}
          {isAdmin() && (
            <>
              <Button
                variant="outlined"
                startIcon={<People />}
                onClick={() => navigate('/app/admin/users')}
              >
                {t('dashboard.manageUsers')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<NotificationsActive />}
                onClick={() => navigate('/app/admin/reminders')}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5a67d8',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              >
                {t('dashboard.reminderAdmin')}
              </Button>
            </>
          )}
          {/* Request to Join Club - for non-pilot users */}
          {user && !user.is_pilot && (
            <Button
              variant="outlined"
              startIcon={<Paragliding />}
              onClick={() => navigate('/registrations')}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#5a67d8',
                  backgroundColor: 'rgba(102, 126, 234, 0.04)',
                },
              }}
            >
              {t('landing.cta.pilotRegistration')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Platform News Section */}
      <Box sx={{ mb: 3 }}>
        <PlatformNews />
      </Box>

      {/* Recent Activity - Compact View */}
      <CompactActivityLog limit={5} />
    </Container>
  );
};

export default DashboardPage;